import { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMutation } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import moment from 'moment'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import { handleValidationsErrorsAPI, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import AdvancedSettings from '~/modules/retailMedia/components/CampaignForm/elements/Advanced'
import AudienceGroup from '~/modules/retailMedia/components/CampaignForm/elements/AudienceGroup'
import Budget from '~/modules/retailMedia/components/CampaignForm/elements/Budget'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'
import { usePrepareCampaignCreateBody } from '~/modules/retailMedia/components/CampaignForm/hooks/usePrepareCampaignCreateBody'
import { useSubmitErrorMessage } from '~/modules/retailMedia/components/CampaignForm/hooks/useSubmitErrorMessage'
import * as S from '~/modules/retailMedia/components/CampaignForm/styles'
import ProductsPanel from '~/modules/retailMedia/components/ProductsPanel'
import {
  createCampaignKey,
  createCampaignService
} from '~/modules/retailMedia/services/campaign'
import { useAppSelector } from '~/store/hooks'

import { CampaignKeywords } from '../../../../elements/CampaignKeywords'
import CampaignType from '../../../../elements/CampaignType'
import Subpublishers from '../../../../elements/Subpublishers'
import { applyPreparedCampaignBody } from '../../../../helpers/applyPreparedCampaignBody'
import { withDefaultAgeRestrictedSetting } from '../../../helpers'
import { DTOCreateProductCampaign } from './dto'

const Product: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const [budgetValueIsValid, setBudgetValueIsValid] = useState(false)
  const { t } = useTranslation()

  const query = useQueryParams()
  const rmid = query.get('rmid') as PublisherId

  const {
    formRef,
    defaultCampaignName,
    audienceId,
    setCampaignPeriod,
    handleDebugSubmit,
    toggleDebugMode
  } = useCampaignForm()
  const { prepareCampaignCreateBody, isLinkedInsertionOrderReady } =
    usePrepareCampaignCreateBody()

  /**
   * Handle products
   */
  const {
    campaign: {
      products: { selectedProducts }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * Data to check costs
   */

  const formDataFinal: Partial<DTOPayloadMinCosts> = useMemo(() => {
    const products = selectedProducts?.map(item => ({
      id: item?.id,
      sku: item?.product_sku || item?.sku,
      categories: item?.categories
    }))

    return {
      name: 'name',
      start_date: moment().format('YYYY-MM-DD'),
      type: 'on_site',
      products
    }
  }, [selectedProducts])

  /**
   * Check data validity
   */

  const formIsValid: FormIsValidResponse = useMemo(() => {
    // product
    const productIsValid = !!formDataFinal?.products?.length

    const inputQueryProduct = formRef?.current?.getFieldRef('query')
    let productIsValidMessage = null

    if (!productIsValid) {
      productIsValidMessage = {
        text: t('rm:campaign.create.products.required'),
        action: () => inputQueryProduct?.focus?.()
      }
    }

    const messages = [productIsValidMessage].filter(item => !!item)

    return {
      isValid: productIsValid,
      messages
    }
  }, [formDataFinal?.products?.length, t])

  /**
   * Form is ready
   * Valor para controlarmos se devemos bloquear o submit
   */
  const formIsReadyToSubmit = useMemo(
    () => budgetValueIsValid && isLinkedInsertionOrderReady,
    [budgetValueIsValid, isLinkedInsertionOrderReady]
  )

  /**
   * Handle request
   */
  const { submitErrorMessage, resolveSubmitError } = useSubmitErrorMessage()

  const [AlertErrorCampaign, setErrorCampaign] = useErrorApiAlert({
    title: submitErrorMessage
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn: createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(resolveSubmitError(error))
    }
  })

  /**
   * Handle submit
   */
  const { lengthCentsCurrency, superAdmin } = useAppInfo()

  const handleSubmit = useCallback(
    async (formData: CreateCampaignProductFormData) => {
      const preData = {
        ...formData,
        products: selectedProducts,
        audience_id: audienceId,
        publisher_id: rmid,
        lengthCentsCurrency
      }

      const body = DTOCreateProductCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(body, prepareCampaignCreateBody, action)
    },
    [
      selectedProducts,
      audienceId,
      rmid,
      lengthCentsCurrency,
      handleDebugSubmit,
      mutate,
      prepareCampaignCreateBody
    ]
  )

  /**
   * Initial data
   */
  const initialData: Partial<CreateCampaignProductFormData> = {
    type: 'product',
    name: defaultCampaignName,
    start_date: moment().format('YYYY-MM-DD'),
    budget: { daily: null },
    placements: { placementsCheck: 'all' },
    audienceToggleWrapper: { audienceToggle: !!audienceId },
    settings: withDefaultAgeRestrictedSetting({
      budget_type: 'fast_as_possible'
    }),
    subpublishers: {
      subpublishersCheck: 'all'
    }
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
      <CampaignType />

      <NameAndTimeInterval
        formRef={formRef}
        labelTitle={t('rm:campaign.create.NameAndTimeInterval.labelTitle')}
        placeholderTitle={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
        )}
        hasDescription
        labelDescription={t(
          'rm:campaign.create.NameAndTimeInterval.labelDescription'
        )}
        placeholderDescription={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
        )}
        onlyDates
        endDateIsRequired={false}
        showValidBadgeOnSection
        intervalAlertMessage={[
          t('rm:campaign.create.NameAndTimeInterval.intervalAlertMessage')
        ]}
        showEndDateOptional
        titlePanel={t('rm:campaign.create.NameAndTimeInterval.titlePanel')}
        onChangeDate={setCampaignPeriod}
      />

      <Subpublishers />

      {!!superAdmin && <CampaignKeywords formRef={formRef} />}

      <AudienceGroup />

      <ProductsPanel
        formRef={formRef}
        publisherId={rmid}
        isRequired
        description={t('rm:campaign.create.ProductsPanel.description')}
      />

      {!!formIsValid.messages?.length && (
        <S.WrapperAlerts>
          {formIsValid.messages.map((message, index) => (
            <Alert
              variant="warning"
              key={index}
              onClick={message?.action}
              role={message?.action ? 'button' : 'text'}
            >
              {message?.text || message}
            </Alert>
          ))}
        </S.WrapperAlerts>
      )}

      <Budget
        adType="product"
        formRef={formRef}
        publisherId={rmid}
        formData={formDataFinal}
        formIsValid={formIsValid}
        handleIsValid={setBudgetValueIsValid}
      />

      <hr />

      <AdvancedSettings />

      <AlertErrorCampaign />

      {!!handleDebugSubmit && (
        <Alert variant="critical">
          🚀 ~ DEBUG MODE ATIVADO: este modo imprime o payload da requisição no
          console e desabilita o envio do formulário.
        </Alert>
      )}

      <div className="form-default__buttons">
        {!!handleDebugSubmit && (
          <Button
            type="button"
            text={'Desativar modo debug'}
            onClick={toggleDebugMode}
            size="large"
            template="critical"
          />
        )}

        <Button
          type="submit"
          text={t('rm:createCampaign')}
          loading={isPending}
          size="large"
          template="primary"
          disabled={!formIsReadyToSubmit}
        />
      </div>
    </Form>
  )
}

export default Product
